<!-- src/SideWinder.Web/src/components/PnlPerformanceMini.vue -->
<script setup lang="ts">
// One cell of the P&L pane's performance row (spec 2026-09-20-pnl-
// performance-charts-design.md section 3): 1M booked sparkline + pill,
// click opens /charts/pnl-<fund>?w=1m. Same self-fetch + goChart pattern
// as GgeClMiniChart, with the SVG drawn as a filled area around 0%.
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { api, type PerformanceFund, type PerformanceSeriesResponse } from '../api/client'
import { fmtPct, pctClass, sparkAreaPaths, PCT_UP, PCT_DOWN } from '../lib/percentBaseline'

const props = defineProps<{ fund: PerformanceFund }>()

const data = ref<PerformanceSeriesResponse | null>(null)
const W = 100
const H = 34

async function load() {
  try { data.value = await api.bookedPerformance(props.fund, '1m') } catch { /* keep last good */ }
}

let timer: ReturnType<typeof setInterval> | null = null
onMounted(() => { void load(); timer = setInterval(load, 30_000) })
onUnmounted(() => { if (timer) clearInterval(timer) })

const lastPct = computed(() => {
  const p = data.value?.points
  return p && p.length ? p[p.length - 1].pct : null
})
const paths = computed(() => sparkAreaPaths(data.value?.points ?? [], W, H))
const lineColor = computed(() => (lastPct.value ?? 0) >= 0 ? PCT_UP : PCT_DOWN)
const path = computed(() => `/charts/pnl-${props.fund.toLowerCase()}`)

function goChart() {
  if (window.location.pathname === path.value) return
  window.history.pushState({}, '', `${path.value}?w=1m`)
  window.dispatchEvent(new PopStateEvent('popstate'))
}
</script>

<template>
  <div class="perf-mini" :data-testid="`pnl-mini-${props.fund.toLowerCase()}`" @click="goChart">
    <div class="hd">
      <span class="label">{{ props.fund }}</span>
      <span class="pill" :class="pctClass(lastPct)" data-testid="pnl-mini-pill">{{ fmtPct(lastPct) }}</span>
    </div>
    <svg :viewBox="`0 0 ${W} ${H}`" preserveAspectRatio="none" class="spark">
      <line x1="0" :y1="paths.zeroY" :x2="W" :y2="paths.zeroY" stroke="#30363d" stroke-width="0.5" stroke-dasharray="2,2" />
      <template v-if="paths.line">
        <path :d="paths.above" fill="rgba(76,175,80,0.35)" />
        <path :d="paths.below" fill="rgba(248,81,73,0.35)" />
        <path :d="paths.line" fill="none" :stroke="lineColor" stroke-width="1.5" data-testid="spark-line" />
      </template>
    </svg>
    <div class="ft">Strategy · 1M</div>
  </div>
</template>

<style scoped>
.perf-mini { background: #161b22; border: 1px solid #30363d; border-radius: 6px; padding: 6px 8px; cursor: pointer; min-width: 0; }
.perf-mini:hover { border-color: #4caf50; }
.hd { display: flex; justify-content: space-between; align-items: center; font-family: 'JetBrains Mono', monospace; margin-bottom: 2px; }
.label { font-size: 11px; color: #c9d1d9; font-weight: 600; }
.pill { font-size: 10px; padding: 1px 6px; border-radius: 10px; color: #888; background: #0d1117; }
.pill.positive { color: #4caf50; background: #12351d; }
.pill.negative { color: #f85149; background: #3a1518; }
.spark { display: block; width: 100%; height: 34px; }
.ft { font-size: 9px; letter-spacing: 0.06em; text-transform: uppercase; color: #6e7681; margin-top: 4px; font-family: 'JetBrains Mono', monospace; }
</style>
